import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import WithRouter from "../../../hoc/WithRouter";
import WithRoutify from "../../../hoc/WithRoutify";
import { useSelector } from "react-redux";
import {
  fetchNotificationStatus,
  notificationStatus,
  setRxntPendingNotificationCount,
} from "../../../store/NotificationSlice";
import { useNavigate } from 'react-router-dom';
import Buttons from "../../UI/Buttons";
import { useDispatch } from "react-redux";
import { fetchRxntPendingRowsAggregated } from "../../../utils/rxntPendingNotificationUtils";
import RxntLaunchButton from "../../Rxnt/RxntLaunchButton";
import {
  launchParamsFromPendingRow,
  pendingRowCanLaunchWithPatient,
} from "../../../utils/rxntLaunchUtils";

/** RXNT Pending tab: gateway worklist rows aggregated across FullyAuthorized providers. */

function rxntPatientName(rx) {
  const p = rx?.patient;
  if (!p) return '—';
  if (p.fullName) return p.fullName;
  return [p.firstName, p.lastName].filter(Boolean).join(' ') || '—';
}

function rxntDrugLabel(rx) {
  return rx?.drug?.drugName || '—';
}

function rxntFormatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
}

function rxntDateString(rx) {
  return rxntFormatDate(rx?.entryDate || rx?.approvedDate || rx?.lastFilledDate || null);
}

/** Snap sent time when present, else RX entry (for optional UI rows) */
function rxntSecondaryDateString(local, rx) {
  return rxntFormatDate(local?.sent_at || rx?.entryDate || rx?.approvedDate || null);
}

function rxntPharmacyName(rx, local) {
  return local?.pharmacy_name || rx?.pharmacy?.name || '—';
}

/** One line: muted label + value (truncates on small screens) */
function RxntDetailRow({ label, value, title }) {
  const v = value != null && String(value).trim() !== '' ? value : '—';
  return (
    <div className="d-flex flex-wrap align-items-baseline mb-1 small" title={title || (typeof v === 'string' ? v : undefined)}>
      <span className="text-muted flex-shrink-0 mr-1" style={{ minWidth: '7.5rem' }}>
        {label}:
      </span>
      <span className="text-dark font-weight-bold text-truncate min-w-0" style={{ maxWidth: '250px' }}>{v}</span>
    </div>
  );
}

const NotificationMessage = (props) => {
    const { modleToogleHandler, Toast } = props;
    const {notification,pendingCount} = useSelector(notificationStatus);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [activeTab, setActiveTab] = useState('messages'); // 'messages' | 'rxnt'

    const [rxntLoading, setRxntLoading] = useState(false);
    const [rxntError, setRxntError] = useState(null);
    const [rxntPendingRows, setRxntPendingRows] = useState([]);
    const [rxntLastUpdatedAt, setRxntLastUpdatedAt] = useState(null);
    const rxntRequestRef = useRef({ id: 0, mounted: true });

    useEffect(() => {
      rxntRequestRef.current.mounted = true;
      return () => {
        rxntRequestRef.current.mounted = false;
      };
    }, []);

    const navigateUrl=(url)=>{
        navigate(url);
        modleToogleHandler();
    }

    const fetchRxntPendingAllProviders = async ({ silent } = {}) => {
      const myReqId = ++rxntRequestRef.current.id;
      if (!silent) setRxntLoading(true);
      setRxntError(null);

      try {
        const { rows } = await fetchRxntPendingRowsAggregated();

        if (!rxntRequestRef.current.mounted || rxntRequestRef.current.id !== myReqId) return;
        setRxntPendingRows(rows);
        setRxntLastUpdatedAt(new Date());
        dispatch(setRxntPendingNotificationCount(rows.length));
      } catch (e) {
        if (!rxntRequestRef.current.mounted || rxntRequestRef.current.id !== myReqId) return;
        setRxntError(e?.message || 'Failed to load RXNT pending');
        setRxntPendingRows([]);
        setRxntLastUpdatedAt(null);
        dispatch(setRxntPendingNotificationCount(0));
      } finally {
        if (!rxntRequestRef.current.mounted || rxntRequestRef.current.id !== myReqId) return;
        setRxntLoading(false);
      }
    };

    // Lazy-load RXNT only when user opens RXNT tab
    useEffect(() => {
      if (activeTab !== 'rxnt') return;
      if (rxntPendingRows.length > 0 || rxntLoading) return;
      fetchRxntPendingAllProviders();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    const rxntCount = rxntPendingRows.length;
    const rxntLastUpdatedLabel = useMemo(() => {
      if (!rxntLastUpdatedAt) return '';
      try {
        return rxntLastUpdatedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
      } catch {
        return '';
      }
    }, [rxntLastUpdatedAt]);

    const renderNotifications = () => {
        if (notification.length === 0) {
          return <p className="text-muted">No notifications available.</p>;
        }
      
        return notification.map((item, index) => (
            <div className="sh_cardBox mb-2">
                <div className="sh_cardBody">
                    <div className="d-flex justify-content-between">
                    <h5 className="fw-bold text-dark mb-0 font-weight-bold">{item.title}</h5>
                    <div>
                        <a className="btn btn-link" onClick={()=>navigateUrl(item.href)}>View Details</a>
                    </div>
                    </div>
                
                <small className="mb-0">{item.content} </small>
                </div>

            </div>

        ));
      };

      const renderRxntPending = () => {
        if (rxntLoading) {
          return (
            <div className="d-flex flex-column align-items-center justify-content-center py-5 px-3">
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: '2.25rem', height: '2.25rem' }}
              >
                <span className="sr-only">Loading RXNT pending prescriptions</span>
              </div>
            </div>
          );
        }
        if (rxntError) {
          return (
            <div className="alert alert-warning py-2 mb-0">
              {rxntError}
            </div>
          );
        }
        if (!rxntPendingRows.length) {
          return <p className="text-muted mb-0">No RXNT pending prescriptions.</p>;
        }

        return (
          <div className="notification-list rxnt-pending-notification-list">
            {rxntPendingRows.map((row) => {
              const launch = launchParamsFromPendingRow(row);
              const canOpenPatient = pendingRowCanLaunchWithPatient(row);
              return (
                <div
                  key={row.key}
                  className="sh_cardBox mb-2 border rxnt-pending-card"
                  style={{ borderColor: 'rgba(0,0,0,0.08)' }}
                >
                  <div className="sh_cardBody py-3 px-3">
                    <RxntDetailRow
                      label="Patient name"
                      value={rxntPatientName(row.rx)}
                      title={rxntPatientName(row.rx)}
                    />
                    <RxntDetailRow
                      label="Prescription date"
                      value={rxntDateString(row.rx)}
                    />
                    <RxntDetailRow
                      label="Medication"
                      value={rxntDrugLabel(row.rx)}
                      title={rxntDrugLabel(row.rx)}
                    />
                    <RxntDetailRow
                      label="Pharmacy"
                      value={rxntPharmacyName(row.rx, row.local)}
                      title={rxntPharmacyName(row.rx, row.local)}
                    />
                    <RxntDetailRow
                      label="Prescriber"
                      value={row.provider_name}
                      title={row.provider_name}
                    />
                    {/* Per-row launch: same prescriber as row + patientData (employee / rxnt / gateway fallback). */}
                    <div className="d-flex justify-content-end mt-2 pt-1">
                      <RxntLaunchButton
                        text="Open in RXNT"
                        providerId={launch.providerId}
                        rxntDoctorId={launch.rxntDoctorId}
                        employeeId={launch.employeeId}
                        rxntPatientId={launch.rxntPatientId}
                        gatewayPatient={launch.gatewayPatient}
                        returnUrl={launch.returnUrl}
                        disabled={!canOpenPatient}
                        disabledTitle="Patient not linked — open RXNT from header or map patient first"
                        onError={(message) => {
                          if (Toast?.error) Toast.error(message, 6000);
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      };
      
    return (
        <>
            <Modal show={true} onHide={modleToogleHandler} dialogClassName='' keyboard={false}
                contentClassName='h-100'
                backdrop="static" size="md">
                <div className="sh_modalBox">
                    <Modal.Header
                    className=" p-2 d-flex align-items-center"
                    closeButton>
                        <div className="d-flex w-100 justify-content-between align-items-center">
                        <Modal.Title>
                            <h6 className="text-lg mb-0 font-weight-bold">
                               Notifications
                            </h6>
 
                        </Modal.Title>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                          {activeTab === 'rxnt' ? (
                            <Buttons
                              className="btn btn-icon btn-transparent-dark py-1 p-0"
                              iconType="Refresh"
                              iconColor="#5a5c69"
                              clickHandler={() => fetchRxntPendingAllProviders()}
                            />
                          ) : (
                            <Buttons
                              className="btn btn-icon btn-transparent-dark py-1 p-0"
                              iconType="Refresh"
                              iconColor="#5a5c69"
                              clickHandler={() => dispatch(fetchNotificationStatus())}
                            />
                          )}
                        </div>
                        
                      </div>
                    </Modal.Header>
                    <Modal.Header>
                    <div className="tabs_wrapForTable mb-2">
                        <ul className="nav nav-borders nav-tabs border-0" role="tablist">
                          <li className="nav-item" role="presentation">
                            <a
                              className={`flex-sm-fill text-sm-center nav-link ${activeTab === 'messages' ? 'active' : ''}`}
                              href="#!"
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveTab('messages');
                              }}
                              role="tab"
                              style={{ cursor: 'pointer' }}
                            >
                              Messages{typeof pendingCount === 'number' && pendingCount > 0 ? ` (${pendingCount})` : ''}
                            </a>
                          </li>
                          <li className="nav-item" role="presentation">
                            <a
                              className={`flex-sm-fill text-sm-center nav-link ${activeTab === 'rxnt' ? 'active' : ''}`}
                              href="#!"
                              onClick={(e) => {
                                e.preventDefault();
                                setActiveTab('rxnt');
                              }}
                              role="tab"
                              style={{ cursor: 'pointer' }}
                            >
                              RXNT Pending{rxntCount > 0 ? ` (${rxntCount})` : ''}
                            </a>
                          </li>
                        </ul>
                      </div>
                    </Modal.Header>
                    <Modal.Body
                      className="notification-modal-body pt-2"
                      style={{ maxHeight: 'min(70vh, 560px)', overflowY: 'auto' }}
                    >

                      {activeTab === 'rxnt' && rxntLastUpdatedLabel ? (
                        <div className="small text-muted mb-2">
                          Updated {rxntLastUpdatedLabel}
                        </div>
                      ) : null}

                      {activeTab === 'messages' ? (
                        <div className="notification-list">{renderNotifications()}</div>
                      ) : (
                        renderRxntPending()
                      )}
                    </Modal.Body>


                </div>
            </Modal>
        </>
    );
};
export default WithRouter(WithRoutify(NotificationMessage));